import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Mic, X, Copy, Download, Settings } from "lucide-react";
import { useVoiceChannel, type VoiceSample } from "./VoiceChannelProvider";
import { VoicePlayerInline } from "./VoicePlayerInline";
import { EchoSettings } from "./EchoSettings";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// Hosted in our own R2 bucket (`my-jarvis-dashboard-erez-voice` under the
// `avatars/` prefix), served via the bucket's public r2.dev URL. Same
// origin as the voice WAV files — one bucket for all dashboard audio
// assets. No cross-deployment dependency.
const AVATAR_BASE = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/avatars";

/**
 * The 128px variant. These portraits are 1024x1024 and are rendered at
 * `w-7 h-7` — 28 pixels. A decoded bitmap costs width x height x 4 bytes
 * whatever the file compresses to, so each one held 4 MB to draw a circle the
 * size of a full stop, one per message: ~48 MB on this page alone.
 *
 * Note the files had already been "optimised" once, for DOWNLOAD size, which
 * moved the memory not at all — JPEG compression does nothing to the decoded
 * bitmap. The visible number got fixed and the one being spent did not budge.
 *
 * The 1024px originals are untouched in the bucket. Same change as the console's
 * copy of this component (tailormind-erez-console, f5ebc90) — one bug, two
 * repos, and the second is only fixed because someone went and looked.
 */
const face = (name: string) => `${AVATAR_BASE}/${name}-128.jpg`;

const AGENT_META: Record<string, { label: string; color: string; avatar: string }> = {
  jarvis: { label: "Jarvis", color: "#2563eb", avatar: face("jarvis") },
  atlas: { label: "Atlas", color: "#ea580c", avatar: face("atlas") },
  ben: { label: "Ben", color: "#0891b2", avatar: face("ben") },
  nova: { label: "Nova", color: "#7c3aed", avatar: face("nova") },
  emma: { label: "Emma", color: "#db2777", avatar: face("emma") },
  iris: { label: "Iris", color: "#65a30d", avatar: face("iris") },
  echo: { label: "Echo", color: "#16a34a", avatar: face("echo") },
  bolt: { label: "Bolt", color: "#f59e0b", avatar: "" },
  spark: { label: "Spark", color: "#ef4444", avatar: "" },
};

// Capitalize first letter; everything else as-is. Used as the display
// fallback when an agent_name isn't in AGENT_META.
function capitalize(s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}

function AgentAvatar({ agentName }: { agentName: string | null }) {
  const key = (agentName || "").toLowerCase();
  const meta = key ? AGENT_META[key] : null;
  if (!meta) {
    // Unknown agent_name. If a name was provided, render an initial-letter
    // circle so the user still sees who sent it; only fall back to the
    // generic mic when there's nothing at all.
    if (agentName && agentName.length > 0) {
      return (
        <div
          className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold ring-1 ring-gray-200"
          style={{ backgroundColor: "#6b7280" }}
        >
          {agentName[0].toUpperCase()}
        </div>
      );
    }
    return (
      <div className="w-7 h-7 rounded-full bg-gray-100 shrink-0 flex items-center justify-center">
        <Mic className="w-3.5 h-3.5 text-gray-400" />
      </div>
    );
  }
  if (meta.avatar) {
    return (
      <img
        src={meta.avatar}
        alt={meta.label}
        className="w-7 h-7 rounded-full shrink-0 object-cover ring-1 ring-gray-200"
      />
    );
  }
  return (
    <div
      className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold ring-1 ring-gray-200"
      style={{ backgroundColor: meta.color }}
    >
      {meta.label[0]}
    </div>
  );
}

function ExpandableText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <p
      className={`text-sm text-[#555] leading-relaxed ${expanded ? "" : "line-clamp-3"} cursor-pointer`}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      {text}
    </p>
  );
}

function AutoplayToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex shrink-0 cursor-pointer select-none items-center gap-1.5 text-[11px] text-muted-foreground">
      <span>Auto-play</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Auto-play new voice messages"
        onClick={() => onChange(!enabled)}
        className={cn(
          "relative h-4 w-7 shrink-0 rounded-full border transition-colors",
          enabled ? "border-foreground bg-foreground" : "border-border bg-muted",
        )}
      >
        <span
          className={cn(
            "absolute top-[1px] h-[10px] w-[10px] rounded-full bg-background transition-transform",
            enabled ? "translate-x-[15px]" : "translate-x-[2px]",
          )}
        />
      </button>
    </label>
  );
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function groupByDay(samples: VoiceSample[]) {
  const groups: Record<string, VoiceSample[]> = {};
  for (const s of samples) {
    const d = new Date(s.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    let label: string;
    if (d.toDateString() === today.toDateString()) label = "TODAY";
    else if (d.toDateString() === yesterday.toDateString()) label = "YESTERDAY";
    else
      label = d
        .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
        .toUpperCase();
    if (!groups[label]) groups[label] = [];
    groups[label].push(s);
  }
  return groups;
}

export function VoicePanelToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Open voice feed"
      onClick={onClick}
      className="relative flex h-9 w-9 items-center justify-center rounded-lg border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <MessageCircle className="h-[18px] w-[18px]" />
    </button>
  );
}

/**
 * Hero image strip at the top of the voice panel (MJOS-116). Renders the
 * most recent `image_url` from the user's voice_samples feed. When a new
 * voice arrives with a fresh image, this cross-fades to the new image over
 * ~250ms. Hidden entirely if no sample in the feed has an image yet —
 * never shows an empty placeholder.
 *
 * Implementation: two stacked <img> layers (front + back). On image change
 * we swap which one is on top and animate the front's opacity. Browsers
 * decode the new URL in the back layer first, so the fade-in shows a
 * fully-painted image rather than the load-in flash you'd get from a
 * single <img>.
 */
function HeroImageStrip({ imageUrl }: { imageUrl: string | null }) {
  // `front` is the layer currently showing; `back` is the layer being
  // prepped to fade in. On a new imageUrl: set `back` = new url, mark it
  // visible, then after the transition completes swap roles so the new
  // image becomes `front` and the slot is free for the next switch.
  const [front, setFront] = useState<string | null>(imageUrl);
  const [back, setBack] = useState<string | null>(null);
  const [backVisible, setBackVisible] = useState(false);
  const prevUrlRef = useRef<string | null>(imageUrl);

  useEffect(() => {
    if (imageUrl === prevUrlRef.current) return;
    prevUrlRef.current = imageUrl;
    if (!imageUrl) {
      // No image — drop both layers immediately, parent will hide the strip.
      setFront(null);
      setBack(null);
      setBackVisible(false);
      return;
    }
    if (!front) {
      // First image — no transition needed.
      setFront(imageUrl);
      return;
    }
    // Cross-fade: load new image on back layer, then fade it in.
    setBack(imageUrl);
    // Next frame: flip the opacity. Two rAFs to be sure the browser has
    // committed the layout before the transition kicks in.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setBackVisible(true));
    });
  }, [imageUrl, front]);

  // After the transition completes, promote `back` to `front` and clear back.
  const handleBackTransitionEnd = () => {
    if (back && backVisible) {
      setFront(back);
      setBack(null);
      setBackVisible(false);
    }
  };

  if (!front) return null;

  return (
    <div className="relative h-[200px] w-full shrink-0 overflow-hidden bg-gray-100">
      <img
        src={front}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      {back && (
        <img
          src={back}
          alt=""
          onTransitionEnd={handleBackTransitionEnd}
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            backVisible ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </div>
  );
}

/**
 * Hover-revealed history strip pinned to the bottom of the feed (MJOS-116).
 * Shows up to 10 thumbnails (newest left) of older `image_url`s for this
 * user, plus a `+N` chip if more exist. Hidden by default (`opacity-0`),
 * fades in when the user's cursor enters the bottom ~80px of the feed or
 * the strip itself. Click a thumbnail = open the full image in a Dialog
 * lightbox.
 */
function ImageHistoryStrip({
  images,
  totalExtra,
}: {
  images: { id: string; url: string }[];
  totalExtra: number;
}) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  if (images.length === 0) return null;

  return (
    <>
      <div
        className={cn(
          "pointer-events-auto absolute inset-x-0 bottom-0 z-10 px-3 pb-2 pt-3",
          // Gradient fade so the strip rises off the white feed.
          "bg-gradient-to-t from-white via-white/95 to-transparent",
          // Hidden by default; revealed when the parent feed's hover-zone
          // (added below) marks `data-hover-bottom="true"`, or when the
          // strip itself is hovered.
          "opacity-0 transition-opacity duration-200 hover:opacity-100",
          "group-data-[hover-bottom=true]/feed:opacity-100",
        )}
      >
        <div className="flex items-end gap-2 overflow-x-auto">
          {images.map((img) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setLightbox(img.url)}
              className="group/thumb relative h-16 w-24 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100 shadow-sm transition-transform hover:scale-105 hover:border-gray-300"
              aria-label="Open image"
            >
              <img src={img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
          {totalExtra > 0 && (
            <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-xs font-medium text-gray-400">
              +{totalExtra}
            </div>
          )}
        </div>
      </div>

      <Dialog open={lightbox !== null} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none">
          <DialogTitle className="sr-only">Image preview</DialogTitle>
          {lightbox && (
            <img
              src={lightbox}
              alt=""
              className="max-h-[85vh] w-full rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Width-agnostic body of the voice feed (header + timeline). Used both by
 * `VoicePanel` (desktop 400px column) and by the mobile `<Sheet>` in
 * MobileTopBar (full-width). Parent owns the width / overlay chrome; this
 * component just fills its container with `flex h-full`.
 */
export function VoiceFeedBody({ onClose }: { onClose: () => void }) {
  const { samples, settings, updateSettings } = useVoiceChannel();
  const [showSettings, setShowSettings] = useState(false);
  const [hoverBottom, setHoverBottom] = useState(false);
  const grouped = groupByDay(samples);

  // Pull image_urls from the feed, newest first. Hero = first non-null;
  // history strip = next 10 (excluding the hero), and we count anything
  // beyond as the "+N more" chip.
  const imagedSamples = useMemo(
    () => samples.filter((s) => typeof s.image_url === "string" && s.image_url!.length > 0),
    [samples],
  );
  const heroUrl = imagedSamples[0]?.image_url ?? null;
  const historyImages = useMemo(
    () => imagedSamples.slice(1, 11).map((s) => ({ id: s.id, url: s.image_url! })),
    [imagedSamples],
  );
  const totalExtra = Math.max(0, imagedSamples.length - 1 - historyImages.length);

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Hero image strip — MJOS-116. Hidden when no images exist. */}
      <HeroImageStrip imageUrl={heroUrl} />

      {/* Header */}
      <div className="shrink-0 flex items-center justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h2
            className="text-sm font-semibold text-[#1a1a1a]"
            style={{ fontFamily: '"SF Pro Rounded", "Nunito", system-ui, sans-serif' }}
          >
            Voice Feed
          </h2>
          <p className="text-xs text-[#666]">
            🎙️ {samples.length} message{samples.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AutoplayToggle
            enabled={!!settings.voice_autoplay}
            onChange={(v) => updateSettings({ voice_autoplay: v })}
          />
          <button
            type="button"
            onClick={() => setShowSettings(!showSettings)}
            aria-label="Echo settings"
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
              showSettings
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close voice feed"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content: Settings or Timeline. The `group/feed` + data attribute
          let the bottom history strip listen to a bottom-hover zone laid
          on top of the feed. */}
      <div
        className="group/feed relative flex-1 overflow-hidden"
        data-hover-bottom={hoverBottom ? "true" : "false"}
      >
        <div className="h-full overflow-y-auto px-5 py-3">
          {showSettings ? (
            <div className="h-full flex items-center justify-center">
              <EchoSettings onClose={() => setShowSettings(false)} />
            </div>
          ) : samples.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-4xl mb-3">🎙️</div>
              <p className="text-sm text-[#999]">No voice messages yet</p>
              <p className="text-xs text-[#bbb] mt-1">Your agents&apos; voice messages will appear here</p>
            </div>
          ) : (
            Object.entries(grouped).map(([label, msgs]) => (
              <div key={label}>
                <div className="py-3">
                  <span className="text-xs font-semibold text-[#999] tracking-wider">{label}</span>
                </div>
                <div className="space-y-px">
                  {msgs.map((sample) => {
                    const agentKey = (sample.agent_name || "").toLowerCase();
                    const meta = agentKey ? AGENT_META[agentKey] : null;
                    // Display label: prefer the curated label, fall back to
                    // the capitalized raw agent_name. Never silently hide a
                    // legitimate sender just because they're not in the lookup.
                    const labelText = meta?.label ?? (sample.agent_name ? capitalize(sample.agent_name) : null);
                    return (
                      <div
                        key={sample.id}
                        className="py-3.5 px-3 -mx-3 rounded-xl hover:bg-black/[0.02] transition-colors group space-y-1.5"
                      >
                        <div className="flex items-center gap-2">
                          <AgentAvatar agentName={sample.agent_name} />
                          {labelText && (
                            <span className="text-sm font-medium text-[#1a1a1a]">{labelText}</span>
                          )}
                          <span className="text-[10px] text-[#999] tabular-nums">
                            {formatTime(sample.created_at)}
                          </span>
                          <div className="flex items-center gap-0.5 ml-auto transition-opacity opacity-0 group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(sample.text_content);
                              }}
                              className="p-1 rounded-md hover:bg-black/5 text-[#999] hover:text-[#666] transition-colors"
                              title="Copy text"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a
                              href={sample.audio_url}
                              download
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 rounded-md hover:bg-black/5 text-[#999] hover:text-[#666] transition-colors"
                              title="Download audio"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </div>
                        <ExpandableText text={sample.text_content} />
                        <VoicePlayerInline audioUrl={sample.audio_url} durationSeconds={sample.duration_seconds} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Invisible bottom-hover zone — when the cursor enters the bottom
            80px of the feed, fade the history strip in. Pointer-events on
            so the cursor actually triggers it; transparent so it doesn't
            occlude content visually. */}
        <div
          className="absolute inset-x-0 bottom-0 h-20 z-0"
          onMouseEnter={() => setHoverBottom(true)}
          onMouseLeave={() => setHoverBottom(false)}
        />

        <ImageHistoryStrip images={historyImages} totalExtra={totalExtra} />
      </div>
    </div>
  );
}

export function VoicePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <aside
      aria-hidden={!open}
      className={cn(
        "shrink-0 overflow-hidden border-s bg-white transition-[width] duration-200 ease-out",
        open ? "w-[400px]" : "w-0",
      )}
    >
      <div className="w-[400px] h-full">
        <VoiceFeedBody onClose={onClose} />
      </div>
    </aside>
  );
}
